/**
 * React Query key factory for all integration status queries.
 *
 * Rules:
 *  - All keys are prefixed with "integration-status" so you can bulk-invalidate
 *    every integration at once: queryClient.invalidateQueries({ queryKey: ["integration-status"] })
 *  - Per-integration keys are stable strings so callers never have to remember
 *    what the exact key looks like.
 *  - Workspace-scoped keys include the workspaceId as the last segment.
 *
 * Adding a new integration: add a factory function below. Nothing else to change.
 */

export const integrationKeys = {
  /** Invalidates ALL integration status queries */
  all: ["integration-status"] as const,

  /** Google Calendar — workspace-scoped (status is per workspace) */
  calendar: (workspaceId: string) =>
    ["integration-status", "google-calendar", workspaceId] as const,

  /** GitHub — user-scoped (no workspace param) */
  github: () => ["integration-status", "github"] as const,

  /** Slack — user-scoped */
  slack: () => ["integration-status", "slack"] as const,

  /** Slack conversations available to post into — workspace-scoped, since a
   *  workspace can hold several Slack installs. */
  slackChannels: (workspaceId: string) =>
    ["integration-status", "slack-channels", workspaceId] as const,

  /** Gmail — workspace-scoped (connection is per workspace) */
  gmail: (workspaceId: string) =>
    ["integration-status", "gmail", workspaceId] as const,

  /** Closot MCP integration tokens — workspace-scoped */
  mcp: (workspaceId: string) =>
    ["integration-status", "mcp", workspaceId] as const,

  /** Jira — workspace-scoped (connection is per workspace) */
  jira: (workspaceId: string) =>
    ["integration-status", "jira", workspaceId] as const,

  /** Linear — workspace-scoped (connection is per workspace) */
  linear: (workspaceId: string) =>
    ["integration-status", "linear", workspaceId] as const,

  /** Intercom — workspace-scoped (connection is per workspace) */
  intercom: (workspaceId: string) =>
    ["integration-status", "intercom", workspaceId] as const,

  /** Figma — workspace-scoped (connection is per workspace) */
  figma: (workspaceId: string) =>
    ["integration-status", "figma", workspaceId] as const,

  /** Dropbox — workspace-scoped (connection is per workspace) */
  dropbox: (workspaceId: string) =>
    ["integration-status", "dropbox", workspaceId] as const,

  /** Zoom — workspace-scoped (connection is per workspace) */
  zoom: (workspaceId: string) =>
    ["integration-status", "zoom", workspaceId] as const,

  /** Asana — workspace-scoped (connection is per workspace) */
  asana: (workspaceId: string) =>
    ["integration-status", "asana", workspaceId] as const,

  /** Trello — workspace-scoped (connection is per workspace) */
  trello: (workspaceId: string) =>
    ["integration-status", "trello", workspaceId] as const,

  /** Box — workspace-scoped (connection is per workspace) */
  box: (workspaceId: string) =>
    ["integration-status", "box", workspaceId] as const,

  /** ClickUp — workspace-scoped (connection is per workspace) */
  clickup: (workspaceId: string) =>
    ["integration-status", "clickup", workspaceId] as const,

  /** PagerDuty — workspace-scoped (connection is per workspace) */
  pagerduty: (workspaceId: string) =>
    ["integration-status", "pagerduty", workspaceId] as const,

  /** Notion importer — workspace-scoped (one Notion connection per workspace) */
  notion: (workspaceId: string) =>
    ["integration-status", "notion", workspaceId] as const,

  /** GitLab — workspace-scoped (connection is per workspace) */
  gitlab: (workspaceId: string) =>
    ["integration-status", "gitlab", workspaceId] as const,

  /** Confluence — workspace-scoped (connection is per workspace) */
  confluence: (workspaceId: string) =>
    ["integration-status", "confluence", workspaceId] as const,

  /** OneDrive / SharePoint — workspace-scoped (connection is per workspace) */
  onedrive: (workspaceId: string) =>
    ["integration-status", "onedrive", workspaceId] as const,

  /** Sentry — workspace-scoped (connection is per workspace) */
  sentry: (workspaceId: string) =>
    ["integration-status", "sentry", workspaceId] as const,

  /** Salesforce — workspace-scoped (connection is per workspace) */
  salesforce: (workspaceId: string) =>
    ["integration-status", "salesforce", workspaceId] as const,

  /** Microsoft Teams — workspace-scoped (connection is per workspace) */
  teams: (workspaceId: string) =>
    ["integration-status", "teams", workspaceId] as const,

  /** Outlook — workspace-scoped (connection is per workspace) */
  outlook: (workspaceId: string) =>
    ["integration-status", "outlook", workspaceId] as const,

  /** Google Drive — workspace-scoped (connection is per workspace) */
  googleDrive: (workspaceId: string) =>
    ["integration-status", "google-drive", workspaceId] as const,

  /** Connections catalog — the connector manifests (cards + marketplace listings) */
  catalog: () => ["integration-status", "catalog"] as const,
};
